/**
 * src/utils/styles.js - Part 1 (統合完全版)
 * 既存の全スタイルを保持し、新デザインを統合
 * UTF-8 エンコーディング
 */

export const styles = {
    // --- 基盤・全体レイアウト ---
    container: (isMobile) => ({
        // オブジェクトから関数に変更
        minHeight: "100vh",
        background: "#ffffffff", // ※ App.jsx側で上書きしている場合はそちらが優先されます
        fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
        color: "#353535ff",
        // ★ モバイルなら 0px、それ以外は 20px
        padding: isMobile ? "10px" : "20px",
        position: "relative",
    }),

    theme: {
        dark: {
            bg: "#1a191e",
            blockBg: "rgba(255, 255, 255, 0.01)", // 透明度1%
            text: "#ffffff",
            heading: "#3433ad", // 見出し・アイコン
            activeIcon: "#93278f", // アクティブ
            border: "rgba(255, 255, 255, 0.05)",
        },
        light: {
            bg: "#f9fafc",
            blockBg: "#ffffff",
            text: "#686868",
            heading: "#3433ad", // 見出し・アイコン
            activeIcon: "#93278f",
            border: "#f0f0f0",
            shadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
    },

    // --- サイドバー新デザイン ---
    sidebarNew: (collapsed, isDarkMode, theme) => ({
        width: collapsed ? "70px" : "260px",
        background: isDarkMode ?
            "#1a191e" :
            "linear-gradient(-45deg, #2c2d96, #9c2393)",
        borderRight: isDarkMode ? "1px solid rgba(255,255,255,0.05)" : "none",
        color: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 1200,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
    }),

    // --- ブロック新デザイン ---
    blockCardNew: (isDarkMode, theme) => ({
        background: isDarkMode ? theme.dark.blockBg : theme.light.blockBg,
        borderRadius: "16px",
        padding: "24px",
        boxShadow: isDarkMode ? "none" : theme.light.shadow,
        border: isDarkMode ?
            "1px solid " + theme.dark.border :
            "1px solid " + theme.light.border,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        color: isDarkMode ? theme.dark.text : theme.light.text,
        backdropFilter: isDarkMode ? "blur(10px)" : "none",
    }),

    initialScreen: {
        minHeight: "100vh",
        background: "linear-gradient(-45deg, #2c2d96, #9c2393)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        color: "#ffffff",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },
    dashboardWrapper: {
        display: "flex",
        minHeight: "100vh",
        background: "#f9fbfc",
        color: "#333333",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },

    // --- サイドバー (デザインイメージ忠実再現) ---
    sidebar: {
        width: "260px",
        background: "linear-gradient(-45deg, #2c2d96, #9c2393)",
        color: "#ffffff",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 1000,
        transition: "transform 0.3s ease",
    },
    sidebarLogoArea: {
        padding: "30px 20px",
        textAlign: "center",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
    sidebarNav: {
        flex: 1,
        padding: "20px 0",
        overflowY: "auto",
    },
    navItem: (active) => ({
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 25px",
        cursor: "pointer",
        background: active ? "rgba(255,255,255,0.11)" : "transparent",
        borderLeft: active ? "4px solid #ffffff" : "4px solid transparent",
        transition: "all 0.2s ease",
        opacity: active ? 1 : 0.8,
        fontSize: "14px",
        fontWeight: active ? "600" : "400",
        color: "#ffffff",
    }),
    mainArea: {
        flex: 1,
        marginLeft: "260px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },

    // --- ヘッダー関連 ---
    header: {
        textAlign: "center",
        marginBottom: "24px",
        position: "relative",
        background: "#ffffff",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        position: "sticky",
        top: 0,
        zIndex: 900,
    },
    logoutBtn: {
        padding: "6px 12px",
        background: "rgba(255,255,255,0.15)",
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: "15px",
        color: "#333",
        fontSize: "11px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    headerBadge: {
        fontSize: "11px",
        letterSpacing: "3px",
        opacity: 0.8,
        marginBottom: "8px",
    },
    appName: {
        fontSize: "32px",
        fontWeight: "800",
        margin: "0 0 8px 0",
        background: "linear-gradient(135deg, #c9960c 0%, #ffaa00 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        textShadow: "none",
    },
    version: {
        fontSize: "12px",
        opacity: 0.7,
        marginBottom: "8px",
        letterSpacing: "2px",
    },
    subtitle: {
        fontSize: "13px",
        opacity: 0.9,
        marginBottom: "8px",
    },

    // --- ダッシュボード・ブロック構造 ---
    gridLayout: {
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: "24px",
        padding: "24px",
    },

    blockTitle: {
        fontSize: "16px",
        fontWeight: "700",
        marginBottom: "20px",
        color: "#2c2d96",
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },

    /**
     * src/utils/styles.js - Part 2
     */

    // --- ヘッダーコントロール (dashboard2fix.jpg準拠) ---
    headerControlGroup: {
        display: "flex",
        alignItems: "center",
        gap: "15px",
        flexWrap: "wrap",
    },
    headerControlItem: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    headerLabel: {
        fontSize: "10px",
        color: "#888888",
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    headerSelect: {
        padding: "6px 10px", // パディングを微増して文字切れを防止
        borderRadius: "6px",
        border: "1px solid #ddd",
        fontSize: "13px", // 少し大きくして視認性向上
        background: "#ffffff",
        color: "#333333",
        cursor: "pointer",
        height: "38px", // 高さを32pxから38pxに拡大
        lineHeight: "1.2",
    },
    headerInputSmall: {
        padding: "6px 10px",
        borderRadius: "6px",
        border: "1px solid #ddd",
        fontSize: "13px",
        color: "#333333",
        height: "38px", // こちらも同様に拡大
        width: "110px",
    },
    headerInput: {
        padding: "4px 8px",
        borderRadius: "6px",
        border: "1px solid #dddddd",
        fontSize: "12px",
        color: "#333333",
        height: "32px",
        width: "100px",
    },
    dateLapseWrapper: {
        display: "flex",
        alignItems: "center",
        background: "#ffffff", // t03.jpgに合わせて白に
        border: "1px solid #ddd",
        borderRadius: "6px",
        padding: "0",
        height: "38px",
        overflow: "hidden",
        boxSizing: "border-box", // 高さを他と厳密に合わせる
    },
    dateLapseBtn: {
        background: "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 10px",
        color: "#555", // 紫から落ち着いたグレー/紺系へ
        height: "100%",
        transition: "background 0.2s",
        // ホバー時に少し背景色をつける
        borderRight: "1px solid #eee",
        borderLeft: "1px solid #eee",
    },
    dateLapseInput: {
        border: "none",
        background: "transparent",
        fontSize: "13px",
        textAlign: "center",
        width: "130px",
        height: "100%",
        padding: "7px 8px 0 8px", // 4px + 3px = 7px に増やしてテキストを下げる
        color: "#333",
        cursor: "pointer",
        fontWeight: "500",
        boxSizing: "border-box",
    },
    // --- ネイタル詳細パネル (natal.png再現) ---
    natalDetailBox: {
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        marginTop: "10px",
        background: "#b0b0b0", // natal.pngのグレー
        borderRadius: "8px",
        padding: "15px",
        width: "500px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
        zIndex: 1000,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "8px 20px",
        color: "#333",
    },
    natalDetailItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        fontWeight: "500",
    },
    toggleRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    toggleItem: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
    },
    toggleTrack: (active) => ({
        width: "34px",
        height: "18px",
        borderRadius: "10px",
        background: active ? "#4CAF50" : "#cccccc",
        position: "relative",
        transition: "background 0.3s",
    }),
    toggleKnob: (active) => ({
        width: "14px",
        height: "14px",
        borderRadius: "50%",
        background: "#ffffff",
        position: "absolute",
        top: "2px",
        left: active ? "18px" : "2px",
        transition: "left 0.3s",
    }),
    toggleLabel: {
        fontSize: "11px",
        color: "#666666",
        whiteSpace: "nowrap",
    },

    // --- 既存UIパーツ (維持) ---
    accuracy: {
        fontSize: "11px",
        opacity: 0.7,
        padding: "4px 12px",
        background: "rgba(255,255,255,0.95)",
        color: "#333",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderRadius: "20px",
        display: "inline-block",
    },
    card: {
        maxWidth: "400px",
        margin: "0 auto 24px",
        background: "rgba(255,255,255,0.95)",
        borderRadius: "16px",
        padding: "24px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    },
    cardTitle: {
        color: "#764ba2",
        fontSize: "15px",
        fontWeight: "600",
        marginBottom: "16px",
    },
    inputGroup: {
        marginBottom: "14px",
    },
    label: {
        display: "block",
        fontSize: "12px",
        color: "#666",
        marginBottom: "4px",
    },
    input: {
        width: "100%",
        padding: "10px 12px",
        borderRadius: "8px",
        border: "1px solid rgba(176, 176, 176, 0.5)",
        fontSize: "14px",
        color: "#333",
        boxSizing: "border-box",
    },
    row: {
        display: "flex",
        gap: "12px",
        marginBottom: "16px",
    },
    button: {
        width: "100%",
        padding: "14px",
        fontSize: "15px",
        fontWeight: "600",
        color: "#fff",
        background: "linear-gradient(45deg, #2c2d96, #9c2393)",
        border: "none",
        borderRadius: "25px",
        cursor: "pointer",
        boxShadow: "0 4px 15px #2c2d96",
    },

    /**
     * src/utils/styles.js - Part 4
     */

    // --- カレンダー & 月相 (Moon) ---
    calendarDay: {
        display: "flex",
        padding: "14px 16px",
        borderRadius: "10px",
        gap: "20px",
        alignItems: "flex-start",
        background: "#ffffff",
        marginBottom: "10px",
        border: "1px solid #f0f0f0",
        boxShadow: "0 2px 5px rgba(0,0,0,0.02)",
    },
    calendarDateLabel: {
        minWidth: "120px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    calendarEvent: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "12px",
        padding: "6px 10px",
        borderRadius: "6px",
        background: "#f9fbfc",
        color: "#333",
    },
    moonInfo: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        flexWrap: "wrap",
        gap: "20px",
        padding: "20px",
        background: "#f8f9fa",
        borderRadius: "12px",
    },
    moonDistanceBox: {
        padding: "20px",
        background: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        marginBottom: "20px",
    },

    // --- アスペクト検索 & 赤緯 ---
    searchForm: {
        background: "#f4f4f9",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "24px",
        border: "1px solid #eeeeee",
    },
    searchRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "16px",
        flexWrap: "wrap",
    },
    searchButton: {
        padding: "12px 30px",
        background: "linear-gradient(45deg, #2c2d96, #9c2393)",
        color: "#fff",
        border: "none",
        borderRadius: "25px",
        fontWeight: "bold",
        cursor: "pointer",
    },
    declinationRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "10px 14px",
        background: "#ffffff",
        borderRadius: "8px",
        marginBottom: "6px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        fontSize: "13px",
    },

    // --- 銘柄選択 (AssetSelector) ---
    assetSelector: {
        marginBottom: "0",
    },
    assetSelectorHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    },
    assetLabel: {
        fontSize: "15px",
        fontWeight: "700",
        color: "#2c2d96",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    addAssetBtn: {
        padding: "6px 14px",
        background: "#2c2d96",
        border: "none",
        borderRadius: "20px",
        color: "#ffffff",
        fontSize: "11px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    assetSelect: {
        width: "100%",
        padding: "12px",
        borderRadius: "8px",
        border: "1px solid #dddddd",
        fontSize: "14px",
        color: "#333333",
        background: "#ffffff",
        cursor: "pointer",
    },
    assetInfo: {
        marginTop: "16px",
        padding: "16px",
        background: "#f8f9fa",
        borderRadius: "8px",
        fontSize: "12px",
        color: "#555555",
        border: "1px solid #eeeeee",
    },
    deleteAssetBtn: {
        marginTop: "12px",
        padding: "6px 12px",
        background: "#e74c3c",
        border: "none",
        borderRadius: "6px",
        color: "#ffffff",
        fontSize: "10px",
        cursor: "pointer",
    },

    /**
     * src/utils/styles.js - Part 5 (完結版)
     */

    // --- ダッシュボード専用アスペクト一覧 ---
    dashAspectList: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        overflowY: "auto",
        maxHeight: "400px",
    },
    dashAspectItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderRadius: "12px",
        background: "#ffffff",
        border: "1px solid #f0f0f0",
        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    },
    dashAspectMain: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    dashAspectSymbol: {
        fontSize: "22px",
        width: "30px",
        textAlign: "center",
    },
    dashAspectOrb: {
        fontSize: "11px",
        fontWeight: "bold",
        color: "#c9960c",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        background: "rgba(201,150,12,0.08)",
        borderRadius: "8px",
    },

    // 重要日一覧
    importantDatesBox: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "10px",
    },
    dateBadge: {
        padding: "6px 12px",
        background: "#000000b0",

        borderRadius: "20px",
        fontSize: "12px",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },

    // --- 凡例、免責事項、モバイル対応 (dashboard_smart.jpg準拠) ---
    mobileHeader: {
        display: "none",
        background: "linear-gradient(-45deg, #2c2d96, #9c2393)",
        padding: "12px 20px",
        justifyContent: "space-between",
        alignItems: "center",
        color: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 1100,
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    },
    hamburgerBtn: {
        background: "transparent",
        border: "none",
        color: "#fff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
    },
    legend: {
        background: "rgba(0,0,0,0.05)",
        borderRadius: "12px",
        padding: "16px",
        fontSize: "11px",
        display: "flex",
        flexWrap: "wrap",
        gap: "15px",
        justifyContent: "center",
        marginTop: "30px",
        color: "#666",
    },
    disclaimer: {
        textAlign: "center",
        marginTop: "30px",
        fontSize: "10px",
        opacity: 0.6,
        color: "#999",
        padding: "0 20px",
    },
    /* src/utils/styles.js の末尾（export const styles = { ... } の中）に追加 */
    // --- フォーム共通スタイル (t04.png準拠) ---
    formControl: {
        width: "100%",
        padding: "8px 12px",
        borderRadius: "6px",
        border: "1px solid #dee2e6",
        fontSize: "13px",
        marginBottom: "10px",
        boxSizing: "border-box",
    },
    formLabel: {
        display: "block",
        fontSize: "12px",
        fontWeight: "600",
        color: "#495057",
        marginBottom: "4px",
        textAlign: "left",
    },
    aspectTableCell: (isUpperTriangle, isDarkMode) => ({
        // ダークモード時は境界線をより薄い透過白に変更
        border: isDarkMode ?
            "1px solid rgba(255, 255, 255, 0.05)" :
            "1px solid #f0f0f0",
        padding: "5px",
        textAlign: "center",
        // ダークモード時の背景色：上半分は透明、下半分（重複箇所）はごく薄い透過白
        backgroundColor: isUpperTriangle ?
            isDarkMode ?
            "transparent" :
            "#ffffff" :
            isDarkMode ?
            "rgba(255, 255, 255, 0.02)" :
            "#f8fafc",
        minWidth: "34px",
        height: "34px",
        fontSize: "16px",
        // テキスト色をダークモード時は白に固定
        color: isDarkMode ? "#ffffff" : "#333333",
    }),
    chartSelect: {
        padding: "6px 15px",
        borderRadius: "40px",
        border: "1px solid #ddd",
        fontSize: "12px",
        background: "#fff",
        cursor: "pointer",
        height: "32px",
        minWidth: "140px",
        textAlign: "center",
    },
    /** * src/utils/styles.js 修正箇所
     * フォーム用のスタイル定義を追加
     */
    addAssetForm: {
        marginTop: "20px",
        padding: "20px",
        background: "#f8fafc",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
    },
    addAssetFormTitle: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#1e293b",
        marginBottom: "15px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    addAssetRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr", // 基本2列
        gap: "12px",
        marginBottom: "12px",
    },
    addAssetField: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    addAssetLabel: {
        fontSize: "11px",
        color: "#64748b",
        fontWeight: "bold",
        marginBottom: "2px",
    },
    addAssetInput: (isMobile) => ({
        width: "100%",
        padding: isMobile ? "12px" : "10px", // 少し高くする
        fontSize: isMobile ? "16px" : "13px", // 16pxにするとiPhoneでズームされない
        border: "1px solid #cbd5e1",
        borderRadius: "8px",

        outline: "none",
        background: "#ffffff",
        boxSizing: "border-box",
    }),
    addAssetSubmitBtn: (isMobile) => ({
        width: "100%",
        marginTop: "10px",
        padding: isMobile ? "15px" : "12px",
        fontSize: isMobile ? "16px" : "14px",
        fontWeight: "bold",
        color: "#fff",
        background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
        border: "none",
        borderRadius: "10px",
        cursor: "pointer",
        boxShadow: "0 4px 6px -1px rgba(139, 92, 246, 0.3)",
        transition: "opacity 0.2s",
    }),

    // 第2引数に isMobile を追加
    dynamicBlockTitle: (isDarkMode, isMobile) => ({
        display: "flex",
        alignItems: "center",
        gap: "10px",
        // モバイル時はフォントサイズを少し小さく
        fontSize: isMobile ? "16px" : "18px",
        fontWeight: "bold",
        color: isDarkMode ? "#ffffff" : "#1e293b",
        marginBottom: isMobile ? "10px" : "15px",
    }),

    dynamicBlockCard: (isDarkMode, isMobile) => ({
        background: isDarkMode ? "rgba(255, 255, 255, 0.01)" : "#ffffff",
        borderRadius: "16px",
        // ★ モバイル時は 10px、PC時は 24px に切り替え
        padding: isMobile ? "10px" : "24px",
        boxShadow: isDarkMode ?
            "0 4px 6px rgba(0, 0, 0, 0.3)" :
            "0 4px 6px rgba(0, 0, 0, 0.05)",
        border: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.05)" : "#f0f0f0"),
        display: "flex",
        flexDirection: "column",
    }),
};